import * as Cesium from 'cesium';
import type { SatelliteGroupDataType } from '../../../../modelling/types';
import { MAPBOX_STYLES } from './constants';

export type MapTheme =
  | 'default'
  | 'dark'
  | 'light'
  | 'streets'
  | 'satellite'
  | 'outdoors'
  | 'aerialLabels'
  | 'road'
  | keyof typeof MAPBOX_STYLES;

export type TleLines = { line1: string; line2: string };

export type EphemerisPoint = {
  epoch: number;
  x: number;
  y: number;
  z: number;
  vx?: number;
  vy?: number;
  vz?: number;
};

export type BackendOrbitTrack = {
  orbitPoints: number[];
  pointStride: number;
  coordinateOrder: string[];
  startTime?: number;
  fromUtc?: number | string;
  toUtc?: number | string;
  stepSizeSeconds?: number;
};

export type MapSatellite = SatelliteGroupDataType['satellites'][number] & {
  tleLines?: TleLines;
  ephemeris?: EphemerisPoint[];
  backendOrbitTrack?: BackendOrbitTrack;
  propagationStatus?: 'preview' | 'propagating' | 'ready' | 'failed';
  active?: boolean;
};

export class DualEntityCollection {
  private primary: Cesium.EntityCollection;
  private getSecondary: () => Cesium.EntityCollection | null | undefined;

  constructor(primary: Cesium.EntityCollection, getSecondary: () => Cesium.EntityCollection | null | undefined) {
    this.primary = primary;
    this.getSecondary = getSecondary;
  }

  add(options: any) {
    const primaryEntity = this.primary.add(options);
    const sec = this.getSecondary();
    if (sec) {
      try {
        sec.add(new Cesium.Entity(primaryEntity));
      } catch (err) {
        console.error('Error adding secondary entity:', err);
      }
    }
    return primaryEntity;
  }

  remove(entityOrId: any) {
    const id = typeof entityOrId === 'string' ? entityOrId : entityOrId.id;
    const res = this.primary.removeById(id);
    const sec = this.getSecondary();
    if (sec) {
      sec.removeById(id);
    }
    return res;
  }

  removeById(id: string) {
    const res = this.primary.removeById(id);
    const sec = this.getSecondary();
    if (sec) {
      sec.removeById(id);
    }
    return res;
  }

  getById(id: string) {
    return this.primary.getById(id);
  }

  removeAll() {
    this.primary.removeAll();
    const sec = this.getSecondary();
    if (sec) {
      sec.removeAll();
    }
  }

  get values() {
    return this.primary.values;
  }
}
